import { useState, useEffect, useCallback } from 'react';
import TopNav from './TopNav';
import { useToast } from '../Toast';

const API = process.env.REACT_APP_API_URL || '';

const STATUSES = [
  { key: 'onboarding',  label: 'Onboarding',  badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  { key: 'active',      label: 'Active',      badge: 'bg-green-500/15 text-green-300 border-green-500/30' },
  { key: 'offboarding', label: 'Offboarding', badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  { key: 'offboarded',  label: 'Offboarded',  badge: 'bg-gray-600/40 text-gray-400 border-gray-600' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map(s => [s.key, s.label]));

const LOCATIONS = [
  { key: 'midtown', label: 'Midtown' }, { key: 'northside', label: 'Northside' },
  { key: 'power_mill', label: 'Power Mill' }, { key: 'tampa', label: 'Tampa' }, { key: 'other', label: 'Other' },
];
const LOC_LABEL = Object.fromEntries(LOCATIONS.map(l => [l.key, l.label]));

const EMP_TYPES = [
  { key: 'full_time', label: 'Full-time' }, { key: 'part_time', label: 'Part-time' }, { key: 'contractor', label: 'Contractor' },
];
const EMP_LABEL = Object.fromEntries(EMP_TYPES.map(e => [e.key, e.label]));

const ASSET_STATUS = [
  { key: 'not_issued', label: 'Not issued' }, { key: 'issued', label: 'Issued' }, { key: 'returned', label: 'Returned' },
];

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  if (!y) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusBadge({ status }) {
  const s = STATUSES.find(x => x.key === status) || STATUSES[0];
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${s.badge}`}>{s.label}</span>;
}

function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const complete = total > 0 && done >= total;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-gray-500">Onboarding</span>
        <span className={complete ? 'text-green-400 font-semibold' : 'text-gray-400'}>{done}/{total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-700 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: complete ? '#22c55e' : '#F05A28' }} />
      </div>
    </div>
  );
}

const inputCls = 'w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500';
const labelCls = 'text-gray-400 text-xs mb-1 block';

// ── Add / edit profile ────────────────────────────────────────────────────────

function EmployeeModal({ employee, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = !!employee;
  const [f, setF] = useState({
    name: employee?.name || '', position: employee?.position || '', department: employee?.department || '',
    location: employee?.location || '', employment_type: employee?.employment_type || '',
    start_date: employee?.start_date ? String(employee.start_date).slice(0, 10) : '',
    manager_name: employee?.manager_name || '', personal_email: employee?.personal_email || '',
    phone: employee?.phone || '', emergency_contact_name: employee?.emergency_contact_name || '',
    emergency_contact_phone: employee?.emergency_contact_phone || '',
    user_id: employee?.user_id ? String(employee.user_id) : '', notes: employee?.notes || '',
  });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    fetch(`${API}/api/onboarding/users`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : []).then(setUsers).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error('Name is required.'); return; }
    setSaving(true);
    try {
      const url = isEdit ? `${API}/api/onboarding/employees/${employee.id}` : `${API}/api/onboarding/employees`;
      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not save.');
      toast.success(isEdit ? 'Saved.' : 'Employee added.');
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
          <h2 className="text-white font-semibold">{isEdit ? 'Edit Employee' : 'Add Employee'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-4">
          <div>
            <label className={labelCls}>Name *</label>
            <input className={inputCls} value={f.name} onChange={e => set('name', e.target.value)} placeholder="Full name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Position</label><input className={inputCls} value={f.position} onChange={e => set('position', e.target.value)} placeholder="e.g. Bartender" /></div>
            <div><label className={labelCls}>Department</label><input className={inputCls} value={f.department} onChange={e => set('department', e.target.value)} placeholder="e.g. Bar" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Location</label>
              <select className={inputCls} value={f.location} onChange={e => set('location', e.target.value)}>
                <option value="">—</option>
                {LOCATIONS.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Employment</label>
              <select className={inputCls} value={f.employment_type} onChange={e => set('employment_type', e.target.value)}>
                <option value="">—</option>
                {EMP_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Start date</label><input type="date" className={inputCls} value={f.start_date} onChange={e => set('start_date', e.target.value)} /></div>
            <div><label className={labelCls}>Manager</label><input className={inputCls} value={f.manager_name} onChange={e => set('manager_name', e.target.value)} placeholder="Supervisor" /></div>
          </div>
          <div>
            <label className={labelCls}>Linked OlogyHQ account</label>
            <select className={inputCls} value={f.user_id} onChange={e => set('user_id', e.target.value)}>
              <option value="">Not linked yet</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
            </select>
            <p className="text-gray-500 text-[11px] mt-1">Link once their login is created during onboarding.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Personal email</label><input className={inputCls} value={f.personal_email} onChange={e => set('personal_email', e.target.value)} /></div>
            <div><label className={labelCls}>Phone</label><input className={inputCls} value={f.phone} onChange={e => set('phone', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Emergency contact</label><input className={inputCls} value={f.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} placeholder="Name" /></div>
            <div><label className={labelCls}>Emergency phone</label><input className={inputCls} value={f.emergency_contact_phone} onChange={e => set('emergency_contact_phone', e.target.value)} /></div>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea className={`${inputCls} resize-none`} rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600 transition">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2 text-white text-sm rounded-lg font-semibold disabled:opacity-50 transition" style={{ backgroundColor: '#F05A28' }}>
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Add Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Checklist section (a phase's milestones) ──────────────────────────────────

function MilestoneSection({ title, phase, tasks, employeeId, canUpload, onChanged }) {
  const toast = useToast();
  const [adding, setAdding] = useState('');
  const done = tasks.filter(t => t.completed).length;

  const toggle = async (task) => {
    try {
      const res = await fetch(`${API}/api/onboarding/tasks/${task.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ completed: !task.completed }),
      });
      if (!res.ok) throw new Error('Could not update.');
      onChanged();
    } catch (err) { toast.error(err.message); }
  };
  const remove = async (task) => {
    try {
      await fetch(`${API}/api/onboarding/tasks/${task.id}`, { method: 'DELETE', credentials: 'include' });
      onChanged();
    } catch { toast.error('Could not remove.'); }
  };
  const add = async () => {
    if (!adding.trim()) return;
    try {
      const res = await fetch(`${API}/api/onboarding/employees/${employeeId}/tasks`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phase, text: adding.trim() }),
      });
      if (!res.ok) throw new Error('Could not add.');
      setAdding(''); onChanged();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-cream text-sm font-semibold">{title}</h3>
        <span className="text-gray-500 text-xs">{done}/{tasks.length}</span>
      </div>
      {tasks.length === 0 ? (
        <p className="text-gray-500 text-xs mb-2">No {phase} steps.</p>
      ) : (
        <ul className="space-y-1 mb-2">
          {tasks.map(t => (
            <li key={t.id} className="flex items-start gap-2 group">
              <button disabled={!canUpload} onClick={() => toggle(t)}
                className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition ${
                  t.completed ? 'bg-green-500 border-green-500' : 'border-gray-500 hover:border-gray-400'
                } disabled:opacity-60`}>
                {t.completed && <span className="text-white text-[10px] leading-none">✓</span>}
              </button>
              <div className="flex-1 min-w-0">
                <span className={`text-sm ${t.completed ? 'text-gray-500 line-through' : 'text-white'}`}>{t.text}</span>
                {t.completed && t.completed_by_name && (
                  <span className="text-gray-600 text-[10px] block">{t.completed_by_name} · {fmtDate(t.completed_at)}</span>
                )}
              </div>
              {canUpload && (
                <button onClick={() => remove(t)} className="text-gray-600 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100 transition flex-shrink-0">Remove</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canUpload && (
        <div className="flex gap-2">
          <input value={adding} onChange={e => setAdding(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }}
            placeholder="Add a step…" className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-orange-500" />
          <button onClick={add} className="px-3 py-1.5 bg-gray-700 text-white text-xs rounded-lg hover:bg-gray-600">Add</button>
        </div>
      )}
    </div>
  );
}

// ── Asset row ─────────────────────────────────────────────────────────────────

function AssetRow({ asset, canUpload, onChanged }) {
  const toast = useToast();
  const [value, setValue] = useState(asset.value || '');
  const patch = async (body) => {
    try {
      const res = await fetch(`${API}/api/onboarding/assets/${asset.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Could not update.');
      onChanged();
    } catch (err) { toast.error(err.message); }
  };
  const statusColor = asset.status === 'issued' ? 'text-green-400' : asset.status === 'returned' ? 'text-gray-400' : 'text-gray-500';

  return (
    <li className="flex flex-wrap items-center gap-2 py-2 border-b border-gray-700/60 last:border-0">
      <span className="text-white text-sm font-medium flex-1 min-w-[100px]">{asset.asset_name}</span>
      {asset.kind === 'value' && (
        <input
          value={value} disabled={!canUpload}
          onChange={e => setValue(e.target.value)}
          onBlur={() => { if ((value || '') !== (asset.value || '')) patch({ value }); }}
          placeholder="code / serial"
          className="w-28 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500 disabled:opacity-60"
        />
      )}
      {canUpload ? (
        <select value={asset.status} onChange={e => patch({ status: e.target.value })}
          className={`bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs focus:outline-none focus:border-orange-500 ${statusColor}`}>
          {ASSET_STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      ) : (
        <span className={`text-xs ${statusColor}`}>{ASSET_STATUS.find(s => s.key === asset.status)?.label}</span>
      )}
      <span className="text-gray-600 text-[10px] w-full sm:w-auto">
        {asset.status === 'issued' && asset.issued_at && `Issued ${fmtDate(asset.issued_at)}`}
        {asset.status === 'returned' && asset.returned_at && `Returned ${fmtDate(asset.returned_at)}`}
      </span>
    </li>
  );
}

// ── Employee detail ───────────────────────────────────────────────────────────

function DetailModal({ employeeId, canUpload, reloadKey, onClose, onEdit, onDelete, onChanged }) {
  const toast = useToast();
  const [emp, setEmp] = useState(null);
  const [newAsset, setNewAsset] = useState({ name: '', kind: 'boolean' });

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/onboarding/employees/${employeeId}`, { credentials: 'include' });
    if (r.ok) setEmp(await r.json());
  }, [employeeId]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const refresh = () => { load(); onChanged(); };

  const setStatus = async (status) => {
    try {
      const res = await fetch(`${API}/api/onboarding/employees/${employeeId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Could not update status.');
      refresh();
    } catch (err) { toast.error(err.message); }
  };
  const addAsset = async () => {
    if (!newAsset.name.trim()) return;
    try {
      const res = await fetch(`${API}/api/onboarding/employees/${employeeId}/assets`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_name: newAsset.name.trim(), kind: newAsset.kind }),
      });
      if (!res.ok) throw new Error('Could not add item.');
      setNewAsset({ name: '', kind: 'boolean' }); refresh();
    } catch (err) { toast.error(err.message); }
  };

  if (!emp) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-gray-800 rounded-xl border border-gray-700 px-6 py-8 text-gray-400 text-sm">Loading…</div>
      </div>
    );
  }

  const onbTasks = emp.tasks.filter(t => t.phase === 'onboarding');
  const offTasks = emp.tasks.filter(t => t.phase === 'offboarding');
  const showOff = offTasks.length > 0 || emp.status === 'offboarding' || emp.status === 'offboarded';
  const info = [
    ['Position', emp.position], ['Department', emp.department],
    ['Location', LOC_LABEL[emp.location] || emp.location], ['Employment', EMP_LABEL[emp.employment_type] || emp.employment_type],
    ['Start date', fmtDate(emp.start_date)], ['Manager', emp.manager_name],
    ['Personal email', emp.personal_email], ['Phone', emp.phone],
    ['Emergency contact', emp.emergency_contact_name && `${emp.emergency_contact_name}${emp.emergency_contact_phone ? ` · ${emp.emergency_contact_phone}` : ''}`],
    ['OlogyHQ account', emp.linked_user_name ? `${emp.linked_user_name}${emp.linked_user_email ? ` (${emp.linked_user_email})` : ''}` : 'Not linked'],
  ].filter(([, v]) => v);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-white font-semibold text-lg truncate">{emp.name}</h2>
            <StatusBadge status={emp.status} />
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none ml-3 shrink-0">×</button>
        </div>

        <div className="p-4 space-y-5">
          {/* Status + actions */}
          {canUpload && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-gray-400 text-xs">Status</label>
              <select value={emp.status} onChange={e => setStatus(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-orange-500">
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <button onClick={onEdit} className="ml-auto px-3 py-1.5 bg-gray-700 text-white text-xs rounded-lg hover:bg-gray-600">Edit profile</button>
            </div>
          )}

          {/* Profile */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {info.map(([k, v]) => (
              <div key={k}>
                <div className="text-gray-500 text-[10px] uppercase tracking-wider">{k}</div>
                <div className="text-white text-sm break-words">{v}</div>
              </div>
            ))}
          </div>
          {emp.notes && (
            <div>
              <div className="text-gray-500 text-[10px] uppercase tracking-wider mb-0.5">Notes</div>
              <div className="text-gray-300 text-sm whitespace-pre-wrap">{emp.notes}</div>
            </div>
          )}

          <div className="border-t border-gray-700 pt-4">
            <MilestoneSection title="Onboarding milestones" phase="onboarding" tasks={onbTasks} employeeId={employeeId} canUpload={canUpload} onChanged={refresh} />
          </div>

          {showOff && (
            <div className="border-t border-gray-700 pt-4">
              <MilestoneSection title="Offboarding milestones" phase="offboarding" tasks={offTasks} employeeId={employeeId} canUpload={canUpload} onChanged={refresh} />
            </div>
          )}

          {/* Assets */}
          <div className="border-t border-gray-700 pt-4">
            <h3 className="text-cream text-sm font-semibold mb-2">Equipment & Access</h3>
            {emp.assets.length === 0 ? (
              <p className="text-gray-500 text-xs mb-2">No items.</p>
            ) : (
              <ul className="mb-2">
                {emp.assets.map(a => <AssetRow key={a.id} asset={a} canUpload={canUpload} onChanged={refresh} />)}
              </ul>
            )}
            {canUpload && (
              <div className="flex gap-2 mt-2">
                <input value={newAsset.name} onChange={e => setNewAsset(s => ({ ...s, name: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') addAsset(); }}
                  placeholder="Add item…" className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-orange-500" />
                <select value={newAsset.kind} onChange={e => setNewAsset(s => ({ ...s, kind: e.target.value }))}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-orange-500">
                  <option value="boolean">Yes/No</option>
                  <option value="value">Has value</option>
                </select>
                <button onClick={addAsset} className="px-3 py-1.5 bg-gray-700 text-white text-xs rounded-lg hover:bg-gray-600">Add</button>
              </div>
            )}
          </div>

          {canUpload && (
            <div className="border-t border-gray-700 pt-3">
              <button onClick={onDelete} className="text-red-500 hover:text-red-400 text-xs">Delete employee</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Manage: templates + asset catalog ─────────────────────────────────────────

function ManageView() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [newT, setNewT] = useState({ onboarding: '', offboarding: '' });
  const [newAsset, setNewAsset] = useState({ name: '', kind: 'boolean' });

  const load = useCallback(async () => {
    const [t, a] = await Promise.all([
      fetch(`${API}/api/onboarding/templates`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/api/onboarding/asset-types`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
    ]);
    setTemplates(t); setAssetTypes(a);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addTemplate = async (phase) => {
    const text = newT[phase].trim();
    if (!text) return;
    try {
      const res = await fetch(`${API}/api/onboarding/templates`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phase, text }),
      });
      if (!res.ok) throw new Error('Could not add.');
      setNewT(s => ({ ...s, [phase]: '' })); load();
    } catch (err) { toast.error(err.message); }
  };
  const delTemplate = async (id) => { await fetch(`${API}/api/onboarding/templates/${id}`, { method: 'DELETE', credentials: 'include' }); load(); };
  const addAssetType = async () => {
    if (!newAsset.name.trim()) return;
    try {
      const res = await fetch(`${API}/api/onboarding/asset-types`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: newAsset.name.trim(), kind: newAsset.kind }),
      });
      if (!res.ok) throw new Error('Could not add.');
      setNewAsset({ name: '', kind: 'boolean' }); load();
    } catch (err) { toast.error(err.message); }
  };
  const delAssetType = async (id) => { await fetch(`${API}/api/onboarding/asset-types/${id}`, { method: 'DELETE', credentials: 'include' }); load(); };

  const phaseBlock = (phase, title) => {
    const rows = templates.filter(t => t.phase === phase);
    return (
      <div>
        <h3 className="text-cream text-sm font-semibold mb-2">{title}</h3>
        <ul className="space-y-1 mb-2">
          {rows.map(t => (
            <li key={t.id} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 group">
              <span className="text-white text-sm flex-1">{t.text}</span>
              <button onClick={() => delTemplate(t.id)} className="text-gray-600 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100 transition">Delete</button>
            </li>
          ))}
          {rows.length === 0 && <li className="text-gray-500 text-xs">No steps yet.</li>}
        </ul>
        <div className="flex gap-2">
          <input value={newT[phase]} onChange={e => setNewT(s => ({ ...s, [phase]: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') addTemplate(phase); }}
            placeholder="New step…" className={inputCls} />
          <button onClick={() => addTemplate(phase)} className="px-3 py-2 text-white text-sm rounded-lg whitespace-nowrap" style={{ backgroundColor: '#F05A28' }}>Add</button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <p className="text-gray-400 text-sm">These templates are copied onto each new hire. Editing them here doesn't change people already added.</p>
      {phaseBlock('onboarding', 'Onboarding milestone template')}
      {phaseBlock('offboarding', 'Offboarding milestone template')}
      <div>
        <h3 className="text-cream text-sm font-semibold mb-2">Equipment & access catalog</h3>
        <p className="text-gray-500 text-xs mb-2">Items seeded onto each hire. "Has value" items hold a code or serial (alarm code, laptop SN).</p>
        <ul className="space-y-1 mb-2">
          {assetTypes.map(a => (
            <li key={a.id} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 group">
              <span className="text-white text-sm flex-1">{a.name}</span>
              <span className="text-gray-500 text-[10px] uppercase tracking-wider">{a.kind === 'value' ? 'value' : 'yes/no'}</span>
              <button onClick={() => delAssetType(a.id)} className="text-gray-600 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100 transition">Delete</button>
            </li>
          ))}
          {assetTypes.length === 0 && <li className="text-gray-500 text-xs">No items yet.</li>}
        </ul>
        <div className="flex gap-2">
          <input value={newAsset.name} onChange={e => setNewAsset(s => ({ ...s, name: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') addAssetType(); }}
            placeholder="New item…" className={inputCls} />
          <select value={newAsset.kind} onChange={e => setNewAsset(s => ({ ...s, kind: e.target.value }))} className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
            <option value="boolean">Yes/No</option>
            <option value="value">Has value</option>
          </select>
          <button onClick={addAssetType} className="px-3 py-2 text-white text-sm rounded-lg whitespace-nowrap" style={{ backgroundColor: '#F05A28' }}>Add</button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function Onboarding({ canUpload }) {
  const toast = useToast();
  const [tab, setTab] = useState('roster');
  const [employees, setEmployees] = useState([]);
  const [unlinkedUsers, setUnlinkedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [detailReload, setDetailReload] = useState(0);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/onboarding/employees`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setEmployees(d.records || []);
        setUnlinkedUsers(d.users || []);
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const matchesSearch = (name, extra) => !search ||
    name.toLowerCase().includes(search.toLowerCase()) || (extra || '').toLowerCase().includes(search.toLowerCase());
  const filtered = employees.filter(e =>
    (!statusFilter || e.status === statusFilter) && matchesSearch(e.name, e.position));
  // Users with no onboarding record — only when not filtering by a record status.
  const noRecordUsers = statusFilter ? [] : unlinkedUsers.filter(u => matchesSearch(u.name, u.email));

  const startOnboarding = async (u) => {
    try {
      const res = await fetch(`${API}/api/onboarding/employees`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: u.name, user_id: u.user_id }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not start onboarding.');
      const emp = await res.json();
      toast.success(`Started onboarding for ${u.name}.`);
      await load();
      setDetailId(emp.id);
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this employee and all their onboarding records?')) return;
    try {
      const res = await fetch(`${API}/api/onboarding/employees/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Could not delete.');
      toast.success('Deleted.'); setDetailId(null); load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <TopNav />
      <main className="px-4 sm:px-6 py-8 sm:py-10 max-w-5xl mx-auto">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
          <div>
            <h1 className="text-cream text-3xl font-bold">Onboarding</h1>
            <p className="text-gray-400 text-sm mt-2">Track new-hire progress, milestones, and equipment.</p>
          </div>
          {canUpload && tab === 'roster' && (
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-white text-sm font-semibold shrink-0 self-start" style={{ backgroundColor: '#F05A28' }}>+ Add Employee</button>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => setTab('roster')} className={`px-4 py-2 text-sm font-medium rounded-lg transition ${tab === 'roster' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}>Roster</button>
          {canUpload && <button onClick={() => setTab('manage')} className={`px-4 py-2 text-sm font-medium rounded-lg transition ${tab === 'manage' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}>Manage</button>}
        </div>

        {tab === 'manage' ? <ManageView /> : (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center mb-5">
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or position…"
                className="flex-1 max-w-xs bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500" />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setStatusFilter('')} className={`px-3 py-1.5 rounded-lg text-sm transition ${statusFilter === '' ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>All</button>
                {STATUSES.map(s => (
                  <button key={s.key} onClick={() => setStatusFilter(s.key)} className={`px-3 py-1.5 rounded-lg text-sm transition ${statusFilter === s.key ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>{s.label}</button>
                ))}
              </div>
            </div>

            {loading ? <p className="text-gray-500 text-sm text-center py-12">Loading…</p>
              : filtered.length === 0 && noRecordUsers.length === 0
                ? <p className="text-gray-500 text-sm text-center py-12">{employees.length === 0 && unlinkedUsers.length === 0 ? 'No employees yet. Add your first hire.' : 'No matches.'}</p>
              : (
                <>
                  {filtered.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {filtered.map(e => (
                        <button key={e.id} onClick={() => setDetailId(e.id)} className="text-left bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-gray-500 transition">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <span className="text-white font-medium truncate">{e.name}</span>
                            <StatusBadge status={e.status} />
                          </div>
                          <div className="text-gray-400 text-xs mb-3 truncate">
                            {[e.position, LOC_LABEL[e.location]].filter(Boolean).join(' · ') || '—'}
                          </div>
                          {(e.status === 'onboarding' || e.onb_done < e.onb_total) && e.onb_total > 0
                            ? <ProgressBar done={e.onb_done} total={e.onb_total} />
                            : <div className="text-gray-500 text-[11px]">{e.onb_total > 0 ? '✓ Onboarding complete' : 'No milestones'}</div>}
                          {e.assets_out > 0 && <div className="text-gray-500 text-[11px] mt-2">🔑 {e.assets_out} item{e.assets_out === 1 ? '' : 's'} issued</div>}
                        </button>
                      ))}
                    </div>
                  )}

                  {noRecordUsers.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">OlogyHQ users — no onboarding record</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {noRecordUsers.map(u => (
                          <div key={`u${u.user_id}`} className="bg-gray-800/60 border border-gray-700/70 rounded-xl p-4 flex flex-col">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <span className="text-gray-200 font-medium truncate">{u.name}</span>
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-gray-600 text-gray-500 whitespace-nowrap">No record</span>
                            </div>
                            <div className="text-gray-500 text-xs mb-3 truncate">{u.email}</div>
                            {canUpload && (
                              <button onClick={() => startOnboarding(u)}
                                className="mt-auto self-start px-3 py-1.5 text-xs rounded-lg text-white font-medium hover:opacity-90 transition" style={{ backgroundColor: '#F05A28' }}>
                                Start onboarding
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
          </>
        )}
      </main>

      {detailId != null && (
        <DetailModal
          employeeId={detailId}
          canUpload={canUpload}
          reloadKey={detailReload}
          onClose={() => setDetailId(null)}
          onEdit={() => { const emp = employees.find(x => x.id === detailId); setEditing(emp); }}
          onDelete={() => handleDelete(detailId)}
          onChanged={load}
        />
      )}
      {editing !== undefined && (
        <EmployeeModal
          employee={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); setDetailReload(k => k + 1); }}
        />
      )}
    </div>
  );
}
